<template>
  <div class="container-fluid py-3">
    <div class="card shadow-sm">
      <div class="card-header bg-primary text-white d-flex justify-content-between align-items-center">
        <h5 class="mb-0"><i class="bi bi-currency-exchange me-2"></i>Consulta de Cotações de Moedas</h5>
      </div>
      <div class="card-body">
        <div class="row align-items-start">
          <div class="col-md-4">
            <CampoDataIntervalo
              id="periodoCotacao"
              label="Período de Consulta"
              v-model:modelValueInicio="filtros.dataInicio"
              v-model:modelValueFim="filtros.dataFim"
            />
          </div>
          <div class="col-md-4">
            <CampoBusca
              id="idMoeda"
              label="Moeda"
              v-model="filtros.idMoeda"
              :configuracao-busca="configBuscaMoeda"
              @selecionado="onMoedaSelecionada"
              required
            />
          </div>
          <div class="col-md-4 mt-4 pt-2">
            <div class="d-flex gap-2">
              <button
                class="btn btn-primary flex-grow-1"
                @click="consultar"
                :disabled="loading || !filtros.siglaMoeda || !filtros.dataInicio || !filtros.dataFim"
              >
                <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                <i v-else class="bi bi-search me-2"></i>
                Consultar
              </button>
              <button
                v-if="auth.isAdminInterno && cotacoes.length > 0"
                class="btn btn-success"
                @click="salvarCache"
                :disabled="loading"
                title="Salvar em Cache"
              >
                <i class="bi bi-cloud-arrow-up"></i>
              </button>
            </div>
          </div>
        </div>

        <div v-if="cotacoes.length > 0" class="mt-4">
          <div class="table-responsive">
            <table class="table table-hover table-striped border">
              <thead class="thead-cotacoes">
                <tr>
                  <th>Data</th>
                  <th>Moeda</th>
                  <th class="text-end">Taxa Compra (BRL)</th>
                  <th class="text-end">Taxa Venda (BRL)</th>
                  <th class="text-end">Paridade USD Compra</th>
                  <th class="text-end">Paridade USD Venda</th>
                  <th>Tipo Boletim</th>
                  <th>Fonte</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in cotacoes" :key="item.idCotacaoMoedaDiaria">
                  <td>{{ formatarDataBR(item.dataCotacao) }}</td>
                  <td>{{ item.siglaMoeda }}</td>
                  <td class="text-end">{{ formatarMoeda(item.taxaCompra) }}</td>
                  <td class="text-end">{{ formatarMoeda(item.taxaVenda) }}</td>
                  <td class="text-end">{{ formatarParidade(item.paridadeUsdCompra) }}</td>
                  <td class="text-end">{{ formatarParidade(item.paridadeUsdVenda) }}</td>
                  <td>
                    <span v-if="item.tipoBoletim === 'Fechamento'" class="badge bg-success">
                      <i class="bi bi-check-circle me-1"></i> Fechamento
                    </span>
                    <span v-else class="badge bg-warning text-dark">
                      <i class="bi bi-exclamation-triangle me-1"></i> {{ item.tipoBoletim }}
                    </span>
                  </td>
                  <td><span class="badge bg-info text-dark">{{ obterFonteDisplay(item.fonte) }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-else-if="buscou && !loading" class="alert alert-info mt-4">
          <i class="bi bi-info-circle me-2"></i>
          Nenhuma cotação encontrada para o período e moeda selecionados.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import api from '@/services/api'
import { useMetadataListasStore } from '@/stores/metadataListas'
import CampoDataIntervalo from '@/components/base/campos/CampoDataIntervalo.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import { criarConfigBuscaMoeda } from '@/search-definitions/moedaSearch'
import { useAuthStore } from '@/stores/auth'

import { formatarDataBR } from '@/utils/useDataBR'

const emit = defineEmits(['show-notification'])
const auth = useAuthStore()
const metadataListas = useMetadataListasStore()

const configBuscaMoeda = criarConfigBuscaMoeda()
const fontesCotacao = ref([])

onMounted(async () => {
  fontesCotacao.value = await metadataListas.getListaLookup('MOE_FONTE_COTACAO', 'Sistema')
})

const filtros = reactive({
  dataInicio: '',
  dataFim: '',
  idMoeda: null,
  siglaMoeda: '',
  displayMoeda: '',
  nomeMoeda: ''
})

const loading = ref(false)
const buscou = ref(false)
const cotacoes = ref([])

const onMoedaSelecionada = (item) => {
  if (item) {
    filtros.siglaMoeda = item.codigoISO
    filtros.idMoeda = item.idMoeda
    filtros.displayMoeda = item.codigoISO
    filtros.nomeMoeda = item.nome
  } else {
    filtros.siglaMoeda = ''
    filtros.idMoeda = null
    filtros.displayMoeda = ''
    filtros.nomeMoeda = ''
  }
}

const consultar = async () => {
  if (!filtros.siglaMoeda || !filtros.dataInicio || !filtros.dataFim) {
    emit('show-notification', { message: 'Preencha todos os campos obrigatórios.', type: 'warning' })
    return
  }

  // Validar intervalo máximo de 366 dias
  const MAX_INTERVALO_DIAS = 366
  const dataInicioDt = new Date(filtros.dataInicio + 'T00:00:00')
  const dataFimDt = new Date(filtros.dataFim + 'T00:00:00')
  const diffMs = dataFimDt.getTime() - dataInicioDt.getTime()
  const diffDias = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

  if (diffDias > MAX_INTERVALO_DIAS) {
    // Truncar dataFim para dataInicio + 365 dias
    const novaDataFim = new Date(dataInicioDt)
    novaDataFim.setDate(novaDataFim.getDate() + MAX_INTERVALO_DIAS - 1)
    const ano = novaDataFim.getFullYear()
    const mes = String(novaDataFim.getMonth() + 1).padStart(2, '0')
    const dia = String(novaDataFim.getDate()).padStart(2, '0')
    filtros.dataFim = `${ano}-${mes}-${dia}`

    emit('show-notification', {
      message: `Intervalo máximo é de ${MAX_INTERVALO_DIAS} dias. A data fim foi ajustada para ${dia}/${mes}/${ano}.`,
      type: 'warning'
    })
  }

  loading.value = true
  buscou.value = true
  cotacoes.value = []

  try {
    const params = {
      siglaMoeda: filtros.siglaMoeda,
      dataInicio: filtros.dataInicio,
      dataFim: filtros.dataFim
    }

    const response = await api.get('CotacoesMoeda/periodo', { params })
    cotacoes.value = response.data

    if (cotacoes.value.length === 0) {
      emit('show-notification', { message: 'Nenhuma cotação encontrada.', type: 'info' })
    }
  } catch (error) {
    console.error('Erro ao consultar cotações:', error)
    emit('show-notification', { message: 'Erro ao buscar cotações. Verifique os dados e tente novamente.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const salvarCache = async () => {
  loading.value = true
  try {
    const response = await api.post('CotacoesMoeda/salvar-cache', { cotacoes: cotacoes.value })
    const data = response.data
    emit('show-notification', {
      message: data.message || 'Cotações salvas no cache com sucesso.',
      type: 'success'
    })
  } catch (error) {
    console.error('Erro ao salvar cache:', error)
    const mensagemErro =
      error.response?.data?.message ||
      (error.response?.status === 400 && error.response?.data?.message) ||
      'Erro ao salvar cache.'
    emit('show-notification', { message: mensagemErro, type: 'error' })
  } finally {
    loading.value = false
  }
}

const formatarMoeda = (valor) => {
  if (valor === null || valor === undefined) return '-'
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 10 }).format(valor)
}

const formatarParidade = (valor) => {
  if (valor === null || valor === undefined) return '-'
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 6 }).format(valor)
}

const obterFonteDisplay = (codigoFonte) => {
  if (!codigoFonte) return 'Cache'
  const item = fontesCotacao.value.find(i => String(i.idListaSistemaItem) === String(codigoFonte))
  return item ? item.textoLista : codigoFonte
}
</script>

<style scoped>
.table th {
  font-weight: 600;
}

.thead-cotacoes {
  --thead-bg: #f8f9fa;
  --thead-color: #212529;
}

[data-bs-theme="dark"] .thead-cotacoes {
  --thead-bg: #2b3035;
  --thead-color: #dee2e6;
}

.thead-cotacoes tr {
  background-color: var(--thead-bg);
  color: var(--thead-color);
}

.thead-cotacoes th {
  background-color: var(--thead-bg);
  color: var(--thead-color);
}
</style>
